import { FEATURE_FLAGS } from '../../../../featureFlags';
import { frontendURL } from '../../../../helper/URLHelper';
import {
  LABEL_PERMISSIONS,
  ONBOARDING_GUIDE_PERMISSIONS,
} from '../../../../constants/permissions';
import {
  lockedFeatureRouteName,
  lockedFeatureURL,
} from '../../../../constants/lockedFeatures';

const SECTION_PEOPLE = 'SECTION_PEOPLE';
const SECTION_SERVICE = 'SECTION_SERVICE';
const SECTION_DATA = 'SECTION_DATA';

const CLAUDIA_ONLY_ITEMS = new Set([
  'settings_onboarding_guide',
  'general_settings_index',
  'agent_list',
  'sso_wizard_index',
]);

// Hub admins keep the real inbox list; customers get the entry pointing at the
// upsell page so connecting a channel is a conversation with us, not a dead end.
const inboxItemForClaudiaOnly = (item, accountId, isHubAdmin) =>
  isHubAdmin
    ? item
    : {
        ...item,
        featureFlag: undefined,
        toState: frontendURL(lockedFeatureURL(accountId, 'inbox')),
        toStateName: lockedFeatureRouteName('inbox'),
      };

const claudiaOnlyItems = (allItems, { accountId, isHubAdmin }) =>
  allItems
    .filter(item => {
      if (CLAUDIA_ONLY_ITEMS.has(item.toStateName)) return true;
      // Knowledge Bases is enabled for every account (feature flag
      // removed), so the label settings are always available.
      if (item.toStateName === 'labels_list') return true;
      return item.toStateName === 'settings_inbox_list';
    })
    .map(item =>
      item.toStateName === 'settings_inbox_list'
        ? inboxItemForClaudiaOnly(item, accountId, isHubAdmin)
        : item
    );

const settings = (accountId, claudiaOnly = false, isHubAdmin = false) => {
  const allItems = [
    {
      icon: 'clipboard',
      label: 'ONBOARDING_GUIDE_NAV',
      hasSubMenu: false,
      meta: {
        permissions: ONBOARDING_GUIDE_PERMISSIONS,
      },
      toState: frontendURL(`accounts/${accountId}/settings/onboarding`),
      toStateName: 'settings_onboarding_guide',
    },
    {
      icon: 'briefcase',
      label: 'ACCOUNT_SETTINGS',
      hasSubMenu: false,
      meta: {
        permissions: ['administrator'],
      },
      toState: frontendURL(`accounts/${accountId}/settings/general`),
      toStateName: 'general_settings_index',
    },
    {
      icon: 'credit-card-person',
      label: 'BILLING',
      hasSubMenu: false,
      meta: {
        permissions: ['administrator'],
      },
      toState: frontendURL(`accounts/${accountId}/settings/billing`),
      toStateName: 'billing_settings_index',
      showOnlyOnCloud: true,
    },
    {
      icon: 'key',
      label: 'AUDIT_LOGS',
      hasSubMenu: false,
      meta: {
        permissions: ['administrator'],
      },
      toState: frontendURL(`accounts/${accountId}/settings/audit-logs/list`),
      toStateName: 'auditlogs_list',
      isEnterpriseOnly: true,
      featureFlag: FEATURE_FLAGS.AUDIT_LOGS,
    },
    {
      icon: 'people',
      label: 'AGENTS',
      hasSubMenu: false,
      section: SECTION_PEOPLE,
      meta: {
        permissions: ['administrator'],
      },
      toState: frontendURL(`accounts/${accountId}/settings/agents/list`),
      toStateName: 'agent_list',
      featureFlag: FEATURE_FLAGS.AGENT_MANAGEMENT,
    },
    {
      icon: 'people-team',
      label: 'TEAMS',
      hasSubMenu: false,
      section: SECTION_PEOPLE,
      meta: {
        permissions: ['administrator'],
      },
      toState: frontendURL(`accounts/${accountId}/settings/teams/list`),
      toStateName: 'settings_teams_list',
      featureFlag: FEATURE_FLAGS.TEAM_MANAGEMENT,
    },
    {
      icon: 'scan-person',
      label: 'CUSTOM_ROLES',
      hasSubMenu: false,
      section: SECTION_PEOPLE,
      meta: {
        permissions: ['administrator'],
      },
      toState: frontendURL(`accounts/${accountId}/settings/custom-roles/list`),
      toStateName: 'custom_roles_list',
      isEnterpriseOnly: true,
      beta: true,
    },
    {
      icon: 'availability',
      label: 'CUSTOM_AVAILABILITY',
      hasSubMenu: false,
      section: SECTION_PEOPLE,
      meta: {
        permissions: ['administrator'],
      },
      toState: frontendURL(
        `accounts/${accountId}/settings/custom-availability/list`
      ),
      toStateName: 'availability_list',
    },
    {
      icon: 'mail-inbox-all',
      label: 'INBOXES',
      hasSubMenu: false,
      section: SECTION_SERVICE,
      meta: {
        permissions: ['administrator'],
      },
      toState: frontendURL(`accounts/${accountId}/settings/inboxes/list`),
      toStateName: 'settings_inbox_list',
      featureFlag: FEATURE_FLAGS.INBOX_MANAGEMENT,
    },
    {
      icon: 'automation',
      label: 'AUTOMATION',
      hasSubMenu: false,
      section: SECTION_SERVICE,
      meta: {
        permissions: ['administrator'],
      },
      toState: frontendURL(`accounts/${accountId}/settings/automation/list`),
      toStateName: 'automation_list',
      featureFlag: FEATURE_FLAGS.AUTOMATIONS,
    },
    {
      icon: 'flash-settings',
      label: 'MACROS',
      hasSubMenu: false,
      section: SECTION_SERVICE,
      meta: {
        permissions: ['administrator', 'agent'],
      },
      toState: frontendURL(`accounts/${accountId}/settings/macros`),
      toStateName: 'macros_wrapper',
      featureFlag: FEATURE_FLAGS.MACROS,
    },
    {
      icon: 'chat-multiple',
      label: 'CANNED_RESPONSES',
      hasSubMenu: false,
      section: SECTION_SERVICE,
      meta: {
        permissions: ['administrator', 'agent'],
      },
      toState: frontendURL(
        `accounts/${accountId}/settings/canned-response/list`
      ),
      toStateName: 'canned_list',
      featureFlag: FEATURE_FLAGS.CANNED_RESPONSES,
    },
    // Email Campaigns Domains and Senders moved out of Settings — they live as
    // tabs inside the Campaigns module now (see CampaignsTabs.vue). User feedback:
    // "Email domains e senders deveriam estar dentro de Campaigns, não Settings."
    {
      icon: 'document-list-clock',
      label: 'SLA',
      hasSubMenu: false,
      section: SECTION_SERVICE,
      meta: {
        permissions: ['administrator'],
      },
      toState: frontendURL(`accounts/${accountId}/settings/sla/list`),
      toStateName: 'sla_list',
      newFeatureFlag: 'sla_enabled',
    },
    {
      icon: 'lock-shield',
      label: 'SSO_WIZARD_NAV',
      hasSubMenu: false,
      section: SECTION_SERVICE,
      meta: {
        permissions: ['administrator'],
      },
      toState: frontendURL(`accounts/${accountId}/settings/sso-wizard`),
      toStateName: 'sso_wizard_index',
    },
    {
      icon: 'flash-on',
      label: 'INTEGRATIONS',
      hasSubMenu: false,
      section: SECTION_SERVICE,
      meta: {
        permissions: ['administrator'],
      },
      toState: frontendURL(`accounts/${accountId}/settings/integrations`),
      toStateName: 'settings_applications',
      featureFlag: FEATURE_FLAGS.JIRA,
    },
    {
      icon: 'bot',
      label: 'AGENT_BOTS',
      hasSubMenu: false,
      section: SECTION_SERVICE,
      meta: {
        permissions: ['administrator'],
      },
      globalConfigFlag: 'csmlEditorHost',
      toState: frontendURL(`accounts/${accountId}/settings/agent-bots`),
      toStateName: 'agent_bots',
      featureFlag: FEATURE_FLAGS.AGENT_BOTS,
    },
    {
      icon: 'tag',
      label: 'LABELS',
      hasSubMenu: false,
      section: SECTION_DATA,
      meta: {
        permissions: LABEL_PERMISSIONS,
      },
      toState: frontendURL(`accounts/${accountId}/settings/labels/list`),
      toStateName: 'labels_list',
      featureFlag: FEATURE_FLAGS.LABELS,
    },
    {
      icon: 'clipboard-code',
      label: 'NATIVE_ATTRIBUTES',
      hasSubMenu: false,
      section: SECTION_DATA,
      meta: {
        permissions: ['administrator'],
      },
      toState: frontendURL(
        `accounts/${accountId}/settings/native-attributes/list`
      ),
      toStateName: 'native_attributes_list',
    },
    {
      icon: 'code',
      label: 'CUSTOM_ATTRIBUTES',
      hasSubMenu: false,
      section: SECTION_DATA,
      meta: {
        permissions: ['administrator'],
      },
      toState: frontendURL(
        `accounts/${accountId}/settings/custom-attributes/list`
      ),
      toStateName: 'custom_attributes_list',
      featureFlag: FEATURE_FLAGS.CUSTOM_ATTRIBUTES,
    },
    {
      // Sits right under Custom Attributes: whoever created a field looks for its
      // permissions next to it, not buried at the bottom of Account Settings.
      icon: 'eye-show',
      label: 'CUSTOM_ATTRIBUTE_PERMISSIONS',
      hasSubMenu: false,
      section: SECTION_DATA,
      meta: {
        permissions: ['administrator', 'cx_engineer'],
      },
      toState: frontendURL(
        `accounts/${accountId}/settings/custom-attributes/permissions`
      ),
      toStateName: 'custom_attributes_permissions',
    },
    {
      // A pencil, not a tag: the screen is about filling values in, and the tag
      // icon already belongs to Labels two entries above.
      icon: 'edit',
      label: 'AI_AUTOFILL',
      hasSubMenu: false,
      // Lives with the other data-shaping screens: it fills the very attributes
      // configured just above it, so it belongs next to them and not in a
      // section of its own.
      section: SECTION_DATA,
      meta: {
        permissions: ['administrator'],
      },
      toState: frontendURL(
        `accounts/${accountId}/settings/custom-attributes/ai-tagging`
      ),
      toStateName: 'custom_attributes_ai_tagging',
      newFeatureFlag: 'ai_tagging_and_field_filling',
    },
  ];

  return {
    parentNav: 'settings',
    routes: [
      'settings_onboarding_guide',
      'agent_bots',
      'agent_list',
      'custom_attributes_list',
      'custom_attributes_permissions',
      'custom_attributes_ai_tagging',
      'native_attributes_list',
      'availability_list',
      'automation_list',
      'auditlogs_list',
      'billing_settings_index',
      'canned_list',
      'general_settings_index',
      'general_settings',
      'labels_list',
      'labels_legacy_list',
      'macros_edit',
      'macros_new',
      'macros_wrapper',
      'settings_applications_integration',
      'settings_applications_webhook',
      'settings_applications',
      'settings_inbox_finish',
      'settings_inbox_list',
      'settings_inbox_new',
      'settings_inbox_show',
      'settings_inboxes_add_agents',
      'settings_inboxes_page_channel',
      'settings_integrations_dashboard_apps',
      'settings_integrations_integration',
      'settings_integrations_slack',
      'settings_integrations_webhook',
      'settings_integrations',
      'settings_teams_add_agents',
      'settings_teams_edit',
      'settings_teams_finish',
      'settings_teams_list',
      'settings_teams_new',
      'sla_list',
      'sso_wizard_index',
      'custom_roles_list',
      lockedFeatureRouteName('inbox'),
    ],
    menuItems: claudiaOnly
      ? claudiaOnlyItems(allItems, {
          accountId,
          isHubAdmin,
        })
      : allItems,
  };
};

export default settings;
